import React, { useState } from "react";
import { AlertType, ApplicationScreen, Clinic } from "../models";
import { Box } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";

import { useAlertContext, useClinicContext, useUserContext } from "../contexts";
import { BuildUrl } from "../helpers";
import { useMsal } from "@azure/msal-react";
import {
	apiAccessRequest,
	SETTING_QUEUE_ENABLE_TELEHEALTH
} from "../constants";
import { useCreateAuditLog } from "../hooks";
import { TelehealthCentresTable } from "../components/TelehealthCentre";

export const TelehealthCentres = React.memo(() => {
	const { user, account } = useUserContext();
	const { instance } = useMsal();
	const { clinics, setRefreshClinics } = useClinicContext();
	const { showAlert, clearAlert } = useAlertContext();
	const [isUpdating, setIsUpdating] = useState(false);
	const [isError, setIsError] = useState(false);
	const { createAuditLog } = useCreateAuditLog();

	const callCreateAuditLog = async (clinicId: number, checked: boolean) => {
		try {
			await createAuditLog([
				{
					clinicId: clinicId,
					username: account?.name ?? "",
					applicationScreen: ApplicationScreen.AdminTelehealthCentre,
					action: checked ? "Enabled telehealth" : "Disabled telehealth"
				}
			]);
		} catch (error) {
			console.log("Error creating audit log", error);
		}
	};

	const handleSettingChange = async (event: React.ChangeEvent) => {
		const { id, checked } = event.target as any;
		const clinic = clinics?.find(
			(clinic: Clinic) =>
				clinic.clinicSettings?.find((cs) => cs.id.toString() === id) != null
		);

		if (clinic) {
			const setting = clinic.clinicSettings?.find(
				(cs) => cs.settingName === SETTING_QUEUE_ENABLE_TELEHEALTH
			);

			try {
				setIsUpdating(true);
				setIsError(false);
				clearAlert();

				const requestUrl = BuildUrl("Clinic/ClinicSettings");
				const tokenResponse = await instance.acquireTokenSilent({
					account,
					scopes: apiAccessRequest.scopes
				});
				const headers = {
					Authorization: `Bearer ${tokenResponse.accessToken}`,
					"Content-Type": "application/json"
				};

				const body = [
					{
						id: parseInt(id),
						clinicId: clinic.id,
						settingId: setting?.settingId,
						value: checked ? "1" : "0",
						username: user?.username
					}
				];
				const response = await fetch(requestUrl, {
					method: "PUT",
					headers,
					body: JSON.stringify(body)
				});

				if (!response.ok) {
					throw new Error();
				}
				showAlert({
					message: `Telehealth setting for ${clinic.name} was updated`,
					alertType: AlertType.success,
					timeout: true
				});
				setRefreshClinics(true);
				await callCreateAuditLog(clinic.id, checked);
			} catch (error) {
				showAlert({
					message: `There was an error updating Telehealth setting for ${clinic.name}`,
					alertType: AlertType.error
				});
				setIsError(true);
			} finally {
				setIsUpdating(false);
				setTimeout(() => {
					window.scrollTo(0, 0);
				}, 500);
			}
		}
	};

	if (isUpdating) {
		return (
			<Box className="icon-loading">
				<CircularProgress size={70} />
			</Box>
		);
	} else if (isError) {
		return <></>;
	} else {
		return (
			<TelehealthCentresTable
				clinics={clinics ?? []}
				handleSettingChange={handleSettingChange}
			/>
		);
	}
});
